import React, { useEffect, useState } from 'react';
import { LoadingSpinner } from '@/components';
import api from '@/utils/api';
import './SpotlightPricing.scss';

/**
 * Spotlight pricing admin — single per-hour rate on the smart contract.
 *
 * Replaces the old DB-tier CRUD UI. Every spotlight (post + profile)
 * costs `rate × duration` for the active payment token. DM spotlight
 * pricing remains per-creator and is managed from the creator's own
 * profile fees, not here.
 *
 * Phase 2 (tracked separately) will add tiered + dynamic-most-popular
 * + holiday-window pricing on chain.
 */

interface RateResponse {
  pricePerHourKlv: string;
  maxDurationHours: number;
}

const SpotlightPricingAdmin: React.FC = () => {
  const [currentRate, setCurrentRate] = useState<string | null>(null);
  const [maxDuration, setMaxDuration] = useState<number | null>(null);
  const [inputRate, setInputRate] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchRate();
  }, []);

  const fetchRate = async () => {
    try {
      setIsLoading(true);
      setError('');
      const { data } = await api.get<RateResponse>('/admin/spotlight/rate');
      setCurrentRate(data.pricePerHourKlv);
      setMaxDuration(data.maxDurationHours);
      setInputRate(data.pricePerHourKlv);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load spotlight rate');
      console.error('Error fetching rate:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseFloat(inputRate);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      setError('Enter a positive number.');
      return;
    }
    try {
      setError('');
      setSuccess('');
      setIsSaving(true);
      const { data } = await api.put('/admin/spotlight/rate', { pricePerHourKlv: parsed });
      setCurrentRate(data.pricePerHourKlv);
      setSuccess(`Rate updated. Tx: ${data.txHash.slice(0, 12)}…`);
    } catch (err: any) {
      const message = err.response?.data?.error || err.message || 'Failed to update rate';
      setError(message);
      console.error('Error updating rate:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner size="large" message="Loading spotlight rate…" fullHeight />;
  }

  const inputRateNum = parseFloat(inputRate);
  const previewExamples = Number.isFinite(inputRateNum)
    ? [1, 12, 24, 72].map((hours) => ({ hours, cost: (inputRateNum * hours).toFixed(2) }))
    : [];

  return (
    <div className="spotlight-pricing-admin">
      <div className="admin-header">
        <div>
          <h1>Spotlight Rate</h1>
          <p className="subtitle">
            Single per-hour rate on the smart contract. Every spotlight cost = rate × duration.
          </p>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <span className="alert-icon">⚠️</span>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <span className="alert-icon">✓</span>
          {success}
        </div>
      )}

      <div className="pricing-form-card">
        <h3>Current rate</h3>
        <p className="current-rate">
          <strong>{currentRate ?? '—'} KLV / hour</strong>
          {maxDuration !== null && (
            <span className="muted"> · max duration {maxDuration}h</span>
          )}
        </p>

        <form onSubmit={handleSave}>
          <div className="form-row">
            <div className="form-group">
              <label>New rate (KLV per hour)</label>
              <input
                type="number"
                min="0"
                step="0.000001"
                value={inputRate}
                onChange={(e) => setInputRate(e.target.value)}
                required
              />
            </div>
            <button type="submit" className="btn-primary" disabled={isSaving}>
              {isSaving ? 'Broadcasting…' : 'Save rate'}
            </button>
          </div>

          {previewExamples.length > 0 && (
            <div className="rate-preview">
              <p className="muted">Preview at the new rate:</p>
              <ul>
                {previewExamples.map(({ hours, cost }) => (
                  <li key={hours}>
                    {hours}h spotlight = <strong>{cost} KLV</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </form>

        <div className="rate-notes">
          <p className="muted small">
            Saving here calls <code>updateSpotlightPricePerHour</code> on chain using the
            configured funding wallet. The wallet must be in the contract&apos;s admins
            mapper — if it isn&apos;t, run{' '}
            <code>npm run set:spotlight-price-klv</code> once on the server to bootstrap.
          </p>
          <p className="muted small">
            Tiered / holiday / dynamic-most-popular pricing is Phase 2 work and requires a
            contract upgrade. See the GH issue.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SpotlightPricingAdmin;
